import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Search, Filter, Bookmark, BookmarkCheck, LogOut, LayoutGrid, List, ChevronDown, Eye, FileCheck, CheckCircle2, ArrowUpDown, User } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ASSET_CLASSES = ['All', 'Private Equity', 'Venture Capital', 'Private Debt', 'Venture Debt'];
const STATUSES = ['All', 'Open', 'Partially Subscribed', 'Closing Soon', 'Closed'];

const statusColors = {
  'Open': 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/30',
  'Partially Subscribed': 'bg-amber-500/15 text-amber-400 border border-amber-500/30',
  'Closing Soon': 'bg-red-500/15 text-red-400 border border-red-500/30',
  'Closed': 'bg-[#555]/20 text-[#999] border border-[#555]/30',
};

const ratingColors = {
  'A+': 'text-[#A0743A]',
  'A': 'text-[#C9A96E]',
  'B+': 'text-[#999]',
  'B': 'text-[#777]',
};

function DealCard({ deal, watchlistIds, onToggleWatchlist, ndaSignedDealIds }) {
  const isWatched = watchlistIds.includes(deal.deal_id);
  const hasNDA = ndaSignedDealIds.includes(deal.deal_id);

  return (
    <div
      data-testid={`deal-card-${deal.deal_id}`}
      className="bg-[#FAF8F5] border-l-4 border-[#A0743A] p-6 shadow-lg transition-all duration-300 group hover:shadow-xl"
      style={{ transition: 'transform 0.3s ease, border-color 0.3s ease, box-shadow 0.3s ease' }}
      onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-6px)'; e.currentTarget.style.borderColor = '#A0743A'; }}
      onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.borderColor = ''; }}
    >
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <span className="bg-[#1A1A1A] text-[#A0743A] px-3 py-1 text-[10px] uppercase tracking-wider font-semibold">
              {deal.asset_class}
            </span>
            <span className={`px-3 py-1 text-[10px] uppercase tracking-wider font-semibold ${statusColors[deal.deal_status] || ''}`}>
              {deal.deal_status}
            </span>
          </div>
          <h3 className="text-xl text-[#1A1A1A]" style={{ fontFamily: "'Cormorant Garamond', Cambria, Georgia, serif" }}>
            {deal.deal_name}
          </h3>
          {deal.deal_code && <p className="text-[#555] text-xs mt-1">{deal.deal_code}</p>}
        </div>
        <button
          data-testid={`watchlist-toggle-${deal.deal_id}`}
          onClick={(e) => { e.preventDefault(); onToggleWatchlist(deal.deal_id); }}
          className="text-[#A0743A] hover:text-[#C9A96E] transition-colors p-1"
        >
          {isWatched ? <BookmarkCheck size={20} /> : <Bookmark size={20} />}
        </button>
      </div>

      <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
        <div>
          <p className="text-[#999] text-xs uppercase tracking-wider mb-1">Target Return</p>
          <p className="text-[#1A1A1A] font-semibold">{deal.target_return}</p>
        </div>
        <div>
          <p className="text-[#999] text-xs uppercase tracking-wider mb-1">Deal Size</p>
          <p className="text-[#1A1A1A] font-semibold">{deal.deal_size}</p>
        </div>
        <div>
          <p className="text-[#999] text-xs uppercase tracking-wider mb-1">Tenor</p>
          <p className="text-[#1A1A1A]">{deal.tenor}</p>
        </div>
        <div>
          <p className="text-[#999] text-xs uppercase tracking-wider mb-1">Rating</p>
          <p className={`font-semibold ${ratingColors[deal.sawai_rating] || 'text-[#999]'}`}>{deal.sawai_rating}</p>
        </div>
      </div>

      <div className="flex items-center gap-2 mb-2">
        <span className="text-[#555] text-xs">{deal.sector}</span>
        {deal.geography && <><span className="text-[#999]">&middot;</span><span className="text-[#555] text-xs">{deal.geography}</span></>}
      </div>
      {/* Return disclaimer */}
      <p className="text-[#999] text-[9px] italic mb-4">Target returns only. Not guaranteed. Subject to deal terms.</p>

      <div className="flex items-center justify-between pt-4 border-t border-[#1A1A1A]/10">
        <div className="flex items-center gap-3">
          <span className="text-[#555] text-xs">
            IM: <span className={deal.im_available === 'Yes' ? 'text-emerald-600 font-semibold' : 'text-[#999]'}>{deal.im_available}</span>
          </span>
          {hasNDA ? (
            <span className="flex items-center gap-1 text-emerald-600 text-[10px] uppercase tracking-wider font-semibold" data-testid={`nda-signed-indicator-${deal.deal_id}`}>
              <CheckCircle2 size={11} /> NDA
            </span>
          ) : (
            <span className="flex items-center gap-1 text-amber-500 text-[10px] uppercase tracking-wider font-semibold" data-testid={`nda-required-indicator-${deal.deal_id}`}>
              <FileCheck size={11} /> NDA Required
            </span>
          )}
        </div>
        <Link
          to={`/portal/deal/${deal.deal_id}`}
          data-testid={`deal-view-btn-${deal.deal_id}`}
          className="flex items-center gap-2 text-[#A0743A] text-xs uppercase tracking-wider font-semibold hover:text-[#C9A96E] transition-colors"
        >
          View Details <Eye size={14} />
        </Link>
      </div>
    </div>
  );
}

export default function InvestorPortal() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [deals, setDeals] = useState([]);
  const [watchlistIds, setWatchlistIds] = useState([]);
  const [ndaSignedDealIds, setNdaSignedDealIds] = useState([]);
  const [filterClass, setFilterClass] = useState('All');
  const [filterStatus, setFilterStatus] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('');
  const [sortOrder, setSortOrder] = useState('desc');
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);

  const fetchDeals = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (filterClass !== 'All') params.set('asset_class', filterClass);
    if (filterStatus !== 'All') params.set('deal_status', filterStatus);
    if (searchQuery.trim()) params.set('search', searchQuery.trim());
    if (sortBy) { params.set('sort_by', sortBy); params.set('sort_order', sortOrder); }
    try {
      const res = await fetch(`${API}/deals?${params}`, { credentials: 'include' });
      if (res.ok) setDeals(await res.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [filterClass, filterStatus, searchQuery, sortBy, sortOrder]);

  const fetchWatchlist = useCallback(async () => {
    try {
      const res = await fetch(`${API}/watchlist`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setWatchlistIds(data.watchlist_items.map(i => i.deal_id));
      }
    } catch (e) { console.error(e); }
  }, []);

  const fetchUserNDAs = useCallback(async () => {
    try {
      const res = await fetch(`${API}/ndas/user`, { credentials: 'include' });
      if (res.ok) {
        const ndas = await res.json();
        setNdaSignedDealIds(ndas.map(n => n.deal_id));
      }
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { fetchDeals(); }, [fetchDeals]);
  useEffect(() => { fetchWatchlist(); }, [fetchWatchlist]);
  useEffect(() => { fetchUserNDAs(); }, [fetchUserNDAs]);

  const toggleWatchlist = async (dealId) => {
    if (watchlistIds.includes(dealId)) {
      await fetch(`${API}/watchlist/${dealId}`, { method: 'DELETE', credentials: 'include' });
      setWatchlistIds(prev => prev.filter(id => id !== dealId));
    } else {
      await fetch(`${API}/watchlist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ deal_id: dealId }),
      });
      setWatchlistIds(prev => [...prev, dealId]);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-[#1A1A1A]" data-testid="investor-portal">
      {/* Header */}
      <header className="border-b border-[#A0743A]/20 bg-[#1A1A1A]/95 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-16">
          <Link to="/" className="text-[#A0743A] text-lg tracking-[0.15em]" style={{ fontFamily: "'Cormorant Garamond', Cambria, Georgia, serif" }}>
            SÀWAI CAPITAL
          </Link>
          <div className="flex items-center gap-6">
            <Link to="/portal/watchlist" className="text-[#FAF8F5] hover:text-[#A0743A] transition-colors text-xs uppercase tracking-wider flex items-center gap-2" data-testid="nav-watchlist">
              <Bookmark size={14} /> Watchlist
            </Link>
            <Link to="/portal/profile" className="text-[#FAF8F5] hover:text-[#A0743A] transition-colors text-xs uppercase tracking-wider flex items-center gap-2" data-testid="nav-profile">
              <User size={14} /> Profile
            </Link>
            {['super_admin','admin','deal_manager','content_manager','investor_relations','view_only'].includes(user?.role) && (
              <Link to="/admin" className="text-[#C9A96E] hover:text-[#A0743A] transition-colors text-xs uppercase tracking-wider" data-testid="nav-admin">
                Admin
              </Link>
            )}
            <div className="flex items-center gap-3 pl-6 border-l border-[#A0743A]/20">
              <span className="text-[#999] text-xs hidden sm:block">{user?.name}</span>
              <button onClick={handleLogout} className="text-[#999] hover:text-[#FAF8F5] transition-colors" data-testid="logout-btn">
                <LogOut size={16} />
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-12 py-10">
        {/* Title */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-8">
          <div>
            <p className="text-[#C9A96E] text-sm uppercase tracking-[0.2em] font-semibold mb-2">Deal Discovery</p>
            <h1 className="text-3xl md:text-4xl text-[#FAF8F5] tracking-tight">Active Opportunities</h1>
          </div>
          <button
            data-testid="toggle-filters-btn"
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 border border-[#A0743A]/40 text-[#A0743A] px-5 py-2.5 text-xs uppercase tracking-wider hover:bg-[#A0743A]/10 transition-colors"
          >
            <Filter size={14} /> Filters <ChevronDown size={14} className={`transition-transform ${showFilters ? 'rotate-180' : ''}`} />
          </button>
        </div>

        {/* Search + Sort Bar */}
        <div className="flex flex-col md:flex-row gap-4 mb-8">
          <div className="flex-1 relative">
            <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#A0743A]" />
            <input
              data-testid="deal-search-input"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Search deals by name, sector, geography..."
              className="w-full bg-transparent border border-[#A0743A]/30 text-[#FAF8F5] pl-11 pr-4 py-3 placeholder-[#555] focus:border-[#C9A96E] focus:outline-none text-sm"
            />
          </div>
          <div className="flex gap-2">
            <select
              data-testid="deal-sort-select"
              value={sortBy}
              onChange={e => setSortBy(e.target.value)}
              className="bg-[#1A1A1A] border border-[#A0743A]/30 text-[#FAF8F5] px-4 py-3 text-xs uppercase tracking-wider focus:border-[#C9A96E] focus:outline-none"
            >
              <option value="">Sort: Latest</option>
              <option value="name">Sort: Name</option>
              <option value="rating">Sort: Rating</option>
              <option value="status">Sort: Status</option>
            </select>
            <button
              data-testid="deal-sort-order-btn"
              onClick={() => setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc')}
              className="border border-[#A0743A]/30 text-[#A0743A] px-3 py-3 hover:bg-[#A0743A]/10 transition-colors flex items-center"
              title={sortOrder === 'asc' ? 'Ascending' : 'Descending'}
            >
              <ArrowUpDown size={14} />
            </button>
          </div>
        </div>

        {/* Filter bar */}
        {showFilters && (
          <div data-testid="filter-bar" className="mb-8 p-6 border border-[#A0743A]/20 bg-[#1A1A1A]">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="text-sm tracking-wide text-[#A0743A] mb-3 block">Asset Class</label>
                <div className="flex flex-wrap gap-2">
                  {ASSET_CLASSES.map(cls => (
                    <button
                      key={cls}
                      data-testid={`filter-class-${cls.toLowerCase().replace(/\s+/g, '-')}`}
                      onClick={() => setFilterClass(cls)}
                      className={`px-4 py-2 text-xs uppercase tracking-wider transition-colors ${
                        filterClass === cls
                          ? 'bg-[#A0743A] text-[#FAF8F5]'
                          : 'border border-[#A0743A]/30 text-[#A0743A] hover:bg-[#A0743A]/10'
                      }`}
                    >
                      {cls}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-sm tracking-wide text-[#A0743A] mb-3 block">Status</label>
                <div className="flex flex-wrap gap-2">
                  {STATUSES.map(st => (
                    <button
                      key={st}
                      data-testid={`filter-status-${st.toLowerCase().replace(/\s+/g, '-')}`}
                      onClick={() => setFilterStatus(st)}
                      className={`px-4 py-2 text-xs uppercase tracking-wider transition-colors ${
                        filterStatus === st
                          ? 'bg-[#A0743A] text-[#FAF8F5]'
                          : 'border border-[#A0743A]/30 text-[#A0743A] hover:bg-[#A0743A]/10'
                      }`}
                    >
                      {st}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Deals grid */}
        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-8 h-8 border-2 border-[#A0743A] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : deals.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-[#999] text-lg">No deals match your filters.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="deals-grid">
            {deals.map(deal => (
              <DealCard key={deal.deal_id} deal={deal} watchlistIds={watchlistIds} onToggleWatchlist={toggleWatchlist} ndaSignedDealIds={ndaSignedDealIds} />
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
